import React from "react";
import { Link } from "react-router-dom";
import { Linkedin, Instagram, Facebook, Twitter } from "lucide-react";
import { BRAND, NAV_LINKS, SOCIALS } from "@/lib/content";
import { BrandWordmark } from "@/components/landing/BrandWordmark";

const SOCIAL_ICONS = { Linkedin, Instagram, Facebook, Twitter };

const Footer = () => {
  const year = new Date().getFullYear();
  const go = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#050208] text-[#F9F6F0] pt-20 pb-10 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-4">
              <img
                src={BRAND.logoWhite}
                alt="EMB Consulting Group"
                className="h-16 w-16 object-contain shrink-0"
              />
              <BrandWordmark
                title="EMB Consulting Group"
                subtitle="Financial Planning · Regular Savings"
                containerClassName="inline-block leading-tight"
                titleClassName="font-display text-[#F9F6F0] text-2xl tracking-tight"
                subtitleClassName="text-[#A397B4] uppercase mt-1"
                subtitleStyle={{
                  fontSize: "0.65rem",
                  lineHeight: "1",
                  letterSpacing: "0.04em",
                }}
              />
            </div>
            <p className="mt-6 text-sm text-[#F9F6F0]/60 max-w-sm leading-relaxed font-light">
              Asesoría profesional en finanzas personales, ahorro, inversión y
              protección. Promotores autorizados Allianz México, con claves
              directas en Skandia y Dominion Capital Strategies.
            </p>
          </div>

          <div className="md:col-span-4">
            <div className="text-[11px] tracking-luxury uppercase text-[#E6D5B8] mb-5">
              Navegación
            </div>
            <ul className="grid grid-cols-3 gap-x-4 gap-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.id}>
                  <button
                    data-testid={`footer-link-${l.id}`}
                    onClick={() => go(l.id)}
                    className="text-sm text-[#F9F6F0]/70 hover:text-[#E6D5B8] transition-colors text-left whitespace-nowrap"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="text-[11px] tracking-luxury uppercase text-[#E6D5B8] mb-5">
              Contacto
            </div>
            <ul className="space-y-3 text-sm text-[#F9F6F0]/70">
              <li>
                <a
                  href={`tel:${BRAND.phone}`}
                  className="hover:text-[#E6D5B8] transition-colors"
                  data-testid="footer-phone"
                >
                  {BRAND.phone}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${BRAND.email}`}
                  className="hover:text-[#E6D5B8] transition-colors"
                  data-testid="footer-email"
                >
                  {BRAND.email}
                </a>
              </li>
              <li>{BRAND.address}</li>
            </ul>

            {/* Social links */}
            <div className="mt-6 pt-5 border-t border-white/10">
              <div className="text-[10px] tracking-luxury uppercase text-[#A397B4] mb-3">
                Síguenos
              </div>
              <div
                data-testid="footer-socials"
                className="flex items-center gap-3"
              >
                {SOCIALS.map((s) => {
                  const Icon = SOCIAL_ICONS[s.icon];
                  return (
                    <a
                      key={s.name}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={s.name}
                      data-testid={`footer-social-${s.name.toLowerCase()}`}
                      className="w-9 h-9 flex items-center justify-center border border-white/15 text-[#F9F6F0]/70 hover:text-[#E6D5B8] hover:border-[#E6D5B8]/55 hover:-translate-y-0.5 transition-all duration-300 rounded-sm"
                    >
                      <Icon size={14} strokeWidth={1.7} />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Credentials strip */}
        <div
          data-testid="footer-credentials"
          className="mt-12 pt-8 border-t border-white/10 grid sm:grid-cols-3 gap-6"
        >
          <div>
            <div className="text-[10px] tracking-luxury uppercase text-[#E6D5B8] mb-2">
              Promotoría Allianz México
            </div>
            <div className="font-display text-2xl text-[#F9F6F0]">
              No. {BRAND.allianzPromotoria}
            </div>
          </div>
          <div>
            <div className="text-[10px] tracking-luxury uppercase text-[#E6D5B8] mb-2">
              Cédula B · CNSF
            </div>
            <div className="font-display text-2xl text-[#F9F6F0]">
              {BRAND.founderCedulaNumber}
            </div>
            <div className="text-[10px] text-[#A397B4]/70 mt-1">
              Marco S. Equis · Fundador
            </div>
          </div>
          <div>
            <div className="text-[10px] tracking-luxury uppercase text-[#E6D5B8] mb-2">
              Reconocimiento
            </div>
            <div className="font-display text-2xl text-[#F9F6F0]">
              MDRT · AMASFAC
            </div>
            <div className="text-[10px] text-[#A397B4]/70 mt-1">
              Million Dollar Round Table {BRAND.mdrtYear} · Miembro AMASFAC
            </div>
          </div>
        </div>

        <div className="mt-10 pt-8 border-t border-white/10 flex flex-col lg:flex-row justify-between gap-6">
          <div className="text-xs text-[#A397B4]/80 max-w-3xl leading-relaxed">
            © {year} {BRAND.name}. Todos los derechos reservados. Operamos como
            promotores e intermediarios autorizados (Promotoría Allianz México
            No. {BRAND.allianzPromotoria}). Las instituciones aquí mencionadas
            se encuentran reguladas por las autoridades financieras de México
            (CNBV, CNSF, CONDUSEF) y/o de sus países de origen. La
            contratación de productos financieros y de seguros se realiza
            directamente con la institución correspondiente, conforme a sus
            términos y condiciones.
          </div>
          <div className="flex flex-col gap-3 lg:items-end">
            <nav
              data-testid="footer-legal-nav"
              className="flex flex-wrap gap-x-6 gap-y-2 text-[11px] tracking-luxury uppercase"
            >
              <Link
                to="/insights"
                data-testid="footer-link-insights"
                className="text-[#F9F6F0]/70 hover:text-[#E6D5B8] transition-colors"
              >
                Insights
              </Link>
              <Link
                to="/legal"
                data-testid="footer-link-legal"
                className="text-[#F9F6F0]/70 hover:text-[#E6D5B8] transition-colors"
              >
                Aviso Legal
              </Link>
              <Link
                to="/privacidad"
                data-testid="footer-link-privacy"
                className="text-[#F9F6F0]/70 hover:text-[#E6D5B8] transition-colors"
              >
                Privacidad
              </Link>
            </nav>
            <div className="text-[10px] tracking-luxury uppercase text-[#A397B4]/70">
              Hecho con cuidado en México
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
